import React, { memo, useContext } from 'react';
import {
  func, bool, shape, arrayOf,
} from 'prop-types';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import swal from '@sweetalert/with-react';

import {
  fetchPreviewCategoryVirtualFacetList,
  setPreviewCategoryVirtualFacetToDeleteId,
  setPreviewCategoryVirtualFacetList,
} from 'actions/previewCategoryVirtualFacet';

import { getFacetPayload } from 'util/virtualFacetManagment';

import { previewCategorySelector } from './selectors/previewCategory';
import { productAttributeSelector } from './selectors/productAttribute';
import { categoryFacetSelector } from './selectors/categoryFacet';

import useFacetEffects from './hooks/useFacet';
import usePreviewCategoryVirtualFacet from './hooks/usePreviewCategoryVirtualFacet';
import FacetComponent from '../../../../../components/Facet';
import { AbilityContext } from '../../../../../components/AbilityContext';

import appPermissions from '../../../../../util/appPermissions';
import { getFilterableAttributes } from '../../../../../util/attributesMappers';
import { setFacets } from '../../../../../actions/previewCategory';

const Facet = memo((props) => {
  const abilityContext = useContext(AbilityContext);

  useFacetEffects({
    virtualCategoryFetched: props.previewCategoryState.virtualCategoryFetched,
    virtualCategoryItem: props.previewCategoryState.virtualCategoryItem,
    setFacets: props.setFacets,
  });

  usePreviewCategoryVirtualFacet({
    categoryItemId: props.previewCategoryState.virtualCategoryItem.id,
    categoryFetched: props.previewCategoryState.virtualCategoryFetched,
    categoryUpdated: props.previewCategoryState.virtualCategoryUpdated,

    fetchCategoryVirtualFacetList: props.fetchPreviewCategoryVirtualFacetList,
    categoryVirtualFacetListFetched: props.categoryFacet.fetched,
    categoryVirtualFacetUpdated: props.categoryFacet.updated,
    categoryVirtualFacetDeleted: props.categoryFacet.deleted,
    categoryVirtualFacetCreated: props.categoryFacet.created,
    categoryVirtualFacetFetching: props.categoryFacet.fetching,
  });

  const {
    virtualCategoryItem,
    filterCategory,
  } = props.previewCategoryState;

  const {
    categoriesAttributesFetching,
    categoriesAttributes,
  } = props.productAttributeState;

  const {
    rankingFetching,
    defaultAttributes,
  } = props;

  const noPermissions = !abilityContext.can(
    appPermissions.category.permissions.update,
    appPermissions.category.name,
  );

  const loading = rankingFetching
    || props.categoryFacet.creating
    || props.categoryFacet.updating
    || props.categoryFacet.deleting
    || props.fetchingCategoriesByIds
    || categoriesAttributesFetching;

  const getFacetAttributes = () => getFilterableAttributes(categoriesAttributes);

  const getCategoryFacets = () => {
    const facetAttributes = getFacetAttributes();
    let categoryFacets = [];
    if (
      !isEmpty(props.categoryFacet.list)
      && !isEmpty(categoriesAttributes)
      && !isEmpty(facetAttributes)
    ) {
      categoryFacets = props.categoryFacet.list.map((cF) => {
        if (facetAttributes.some(pASL => pASL.code === cF)) {
          return facetAttributes.find(pASL => pASL.code === cF);
        }
        return false;
      }).filter(a => a);
    }

    const defaultAttributesList = defaultAttributes || [];
    const filteredFacets = categoryFacets.filter(
      cf => !defaultAttributesList.some(da => da.field === cf.code),
    );
    return [...defaultAttributesList, ...filteredFacets, ...props.categoryFacet.list];
  };

  const handleFacetChanges = (facets) => {
    const mappedFacets = facets.filter(f => !f.code && !f.default);
    // eslint-disable-next-line no-param-reassign
    mappedFacets.forEach(i => delete i.groupedValues);
    const newLsit = [{
      ...props.categoryFacet.item,
      facetsList: mappedFacets,
    }];
    props.setPreviewCategoryVirtualFacetList(newLsit);
  };

  const preHandleFacetDelete = () => {
    swal({
      title: 'Are you sure?',
      text: 'Are you sure you want to permanently delete virtual facets?',
      icon: 'warning',
      dangerMode: true,
      buttons: true,
    }).then((willDiscard) => {
      if (willDiscard) {
        props.setPreviewCategoryVirtualFacetToDeleteId(props.categoryFacet.item.id);
        props.setPreviewCategoryVirtualFacetList([]);
      }
    });
  };

  const handleFacetItemDelete = (newList) => {
    handleFacetChanges(newList);
    if (isEmpty(newList.filter(l => !l.default))) {
      props.setPreviewCategoryVirtualFacetToDeleteId(props.categoryFacet.item.id);
    } else {
      props.setPreviewCategoryVirtualFacetToDeleteId('');
    }
  };

  const handleFacetManagementDialogOk = (facet) => {
    const facetPayload = getFacetPayload({
      prevFacet: facet,
      useRev: true,
      nextFacet: {
        item: props.categoryFacet.item,
        list: props.categoryFacet.list,
      },
    });
    props.setPreviewCategoryVirtualFacetList(facetPayload);
  };

  const disabledClass = loading ? 'disabled' : '';

  return (
    <div className={`relative preview-drawer-facet ${disabledClass}`}>
      <FacetComponent
        key={virtualCategoryItem.id}
        entity={{
          type: 'virtual_category',
          id: virtualCategoryItem.id,
        }}
        loading={loading}
        inputLabel="Select"
        list={getFacetAttributes()}
        defaultAttributes={defaultAttributes}
        disabled={isEmpty(filterCategory.mappedCategories) || noPermissions}
        table={{
          tBody: getCategoryFacets(),
        }}
        onAdd={handleFacetChanges}
        onDeleteButton={{
          disabled: isEmpty(props.categoryFacet.list)
            && isEmpty(props.categoryFacet.virtualFacetIdToDelete),
          onClick: preHandleFacetDelete,
        }}
        onItemDelete={handleFacetItemDelete}
        onDragEnd={handleFacetChanges}
        newFacet={{
          onOk: handleFacetManagementDialogOk,
        }}
      />
    </div>
  );
});

Facet.propTypes = {
  defaultAttributes: arrayOf(shape()).isRequired,
  previewCategoryState: shape().isRequired,
  productAttributeState: shape().isRequired,

  rankingFetching: bool.isRequired,

  setFacets: func.isRequired,

  categoryFacet: shape().isRequired,

  fetchPreviewCategoryVirtualFacetList: func.isRequired,

  setPreviewCategoryVirtualFacetToDeleteId: func.isRequired,
  setPreviewCategoryVirtualFacetList: func.isRequired,

  fetchingCategoriesByIds: bool.isRequired,
};

Facet.defaultProps = {};

const mapStateToProps = state => ({
  previewCategoryState: previewCategorySelector(state.previewCategory),
  productAttributeState: productAttributeSelector(state.productAttribute),
  rankingFetching: state.ranking.fetching,
  defaultAttributes: state.defaultAttribute.attributes.categories,

  categoryFacet: categoryFacetSelector(state.previewCategoryVirtualFacet),

  categoryVirtualFacetList: state.previewCategoryVirtualFacet.list,
  categoryVirtualFacetListFetched: state.previewCategoryVirtualFacet.fetched,
  categoryVirtualFacetDeleted: state.previewCategoryVirtualFacet.deleted,
  categoryVirtualFacetUpdating: state.previewCategoryVirtualFacet.updating,
  categoryVirtualFacetUpdated: state.previewCategoryVirtualFacet.updated,
  categoryVirtualFacetCreated: state.previewCategoryVirtualFacet.created,
  categoryVirtualFacetCreating: state.previewCategoryVirtualFacet.creating,

  fetchingCategoriesByIds: state.physicalCategory.fetchingCategoriesByIds,
});

const mapDispatchToProps = {
  setFacets,
  fetchPreviewCategoryVirtualFacetList,
  setPreviewCategoryVirtualFacetToDeleteId,
  setPreviewCategoryVirtualFacetList,
};

export default connect(mapStateToProps, mapDispatchToProps)(Facet);
